import * as React from 'react';

import MetricsBar from 'components/metrics-bar/MetricsBar';
import MetricLabelWithTooltip from './MetricLabelWithTooltip';

import { insertIf } from 'utils/list';
import { __t, applyTranslations } from 'utils/language';
import { getMetricValue, getRawMetricValue } from 'utils/helper/common';
import {
  getFormattedAmount,
  getFormattedPercentageRate
} from 'utils/formatters';
import { getPriceRecommendationRange } from 'utils/helper/price';

import type { Metrics } from 'types';

import './channel-metrics.scss';
import { isBrandsFocusedOrganization } from 'utils/organization';
import { Organization } from 'types';

applyTranslations(require('locales/common/collabary-common.i18n.json'));
applyTranslations(require('./channel-metrics.i18n.json'));

type OwnProps = {
  metrics: Metrics,
  currentOrganization: Organization | undefined | null,
  isFreemium: boolean | null | undefined,
  isSpecialTempInfluencer: boolean
};

type Props = OwnProps;

const ChannelMetricsTT: React.FunctionComponent<Props> = (props: Props) => {
  const {
    metrics,
    currentOrganization,
    isFreemium,
    isSpecialTempInfluencer
  } = props;

  if (Object.keys(metrics).length === 0) {
    return null;
  }

  const isBrand = isBrandsFocusedOrganization(currentOrganization);
  const priceRecoPost = getPriceRecommendationRange(
    getMetricValue(metrics, 'min_price_per_post'),
    getMetricValue(metrics, 'max_price_per_post')
  );

  const genericMetrics = [
    // followers
    // COLLAB-14071 & COLLAB-17556
    ...insertIf(!isSpecialTempInfluencer, {
      key: 'channel-metric-followers-tiktok',
      name: (
        <MetricLabelWithTooltip
          label={__t('channel_metrics.followers')}
          tooltip={__t('channel_metrics.followers_tooltip_tiktok')}
        />
      ),
      value: getFormattedAmount(getRawMetricValue(metrics, 'followers') ?? 0)
    }),
    // Total likes
    {
      key: 'channel-metric-total-likes-tiktok',
      name: (
        <MetricLabelWithTooltip
          label={__t('channel_metrics.total_likes')}
          tooltip={__t('channel_metrics.total_likes_tooltip')}
        />
      ),
      value: getFormattedAmount(getRawMetricValue(metrics, 'total_likes') ?? 0)
    },
    // engagement - followers
    {
      key: 'channel-metric-engagement-followers-tiktok',
      name: (
        <MetricLabelWithTooltip
          label={__t('channel_metrics.engagement_followers')}
          tooltip={__t('channel_metrics.engagement_followers_tiktok_tooltip')}
        />
      ),
      value: getFormattedPercentageRate(getRawMetricValue(metrics, 'followers_engagement'))
    }
  ];

  const videosMetrics = [
    // engagement - views
    {
      key: 'channel-metric-engagement-views-tiktok',
      name: (
        <MetricLabelWithTooltip
          label={__t('channel_metrics.engagement_views')}
          tooltip={__t('channel_metrics.engagement_views_tiktok_tooltip')}
        />
      ),
      value: getFormattedPercentageRate(getRawMetricValue(metrics, 'views_engagement'))
    },

    // Average views
    {
      key: 'channel-metric-average-views-tiktok',
      name: (
        <MetricLabelWithTooltip
          label={__t('channel_metrics.average_views_tiktok')}
          tooltip={__t('channel_metrics.average_views_tiktok_tooltip')}
        />
      ),
      value: getFormattedAmount(getRawMetricValue(metrics, 'avg_views') ?? 0)
    },

    // Average likes
    {
      key: 'channel-metric-average-likes-tiktok',
      name: (
        <MetricLabelWithTooltip
          label={__t('channel_metrics.average_likes')}
          tooltip={__t('channel_metrics.average_likes_tooltip')}
        />
      ),
      value: getFormattedAmount(getRawMetricValue(metrics, 'avg_likes') ?? 0)
    },

    // Average comments
    {
      key: 'channel-metric-average-comments-tiktok',
      name: (
        <MetricLabelWithTooltip
          label={__t('channel_metrics.average_comments')}
          tooltip={__t('channel_metrics.average_comments_tooltip')}
        />
      ),
      value: getFormattedAmount(getRawMetricValue(metrics, 'avg_comments') ?? 0)
    },

    // Average shares
    {
      key: 'channel-metric-average-shares-tiktok',
      name: (
        <MetricLabelWithTooltip
          label={__t('channel_metrics.average_shares')}
          tooltip={__t('channel_metrics.average_shares_tooltip')}
        />
      ),
      value: getFormattedAmount(getRawMetricValue(metrics, 'avg_shares') ?? 0)
    },

    // Videos per week
    {
      key: 'channel-metric-videos-per-week-tiktok',
      name: (
        <MetricLabelWithTooltip
          label={__t('channel_metrics.videos_per_week')}
          tooltip={__t('channel_metrics.videos_per_week_tooltip')}
        />
      ),
      value: getFormattedAmount(getRawMetricValue(metrics, 'videos_per_week'))
    },
    ...insertIf(isBrand && priceRecoPost, {
      key: 'channel-metric-price-video-tiktok',
      name: (
        <MetricLabelWithTooltip
          label={__t('channel_metrics.price_tt_video')}
          tooltip={__t('channel_metrics.price_tt_video_tooltip')}
        />
      ),
      value: isFreemium ? 'xxx-xxx' : priceRecoPost
    })
  ];

  return (
    <React.Fragment>
      <section className="channel-metrics">
        <MetricsBar
          metrics={genericMetrics}
          metricsType={__t('channel_metrics.general_section_label')}
        />
      </section>
      <section className="channel-metrics">
        <MetricsBar
          metrics={videosMetrics}
          metricsType={__t('channel_metrics.videos_section_label')}
        />
      </section>
    </React.Fragment>
  );
};

export default ChannelMetricsTT;
